/**
 * Channel classification (mirrors-classifier).
 *
 * Three questions: how many channels were classified over 1/7/30 days, which
 * countries they are in, and which languages they publish in. All of it comes
 * from the Elasticsearch `channel_classification` index — a terms aggregation
 * over millions of documents answers in ~2.85 s where the equivalent Postgres
 * GROUP BY took 1,221 s.
 */
import { PanelState, Stat, Tile } from '../components/ui'
import { Sparkline } from '../components/charts'
import { headline, n, pct } from '../components/format'
import {
  useClassifiedCounts,
  useClassifierDaily,
  useClassifierDistributions,
  useVideoClassified,
  useVideoDaily,
  useVideoDistributions,
} from '../api/queries'

/** ISO-3166-1 alpha-2 → display name.
 *
 * The API returns codes, not names, because the classifier normalises at write
 * time: Postgres holds the same country under two spellings ('IN' 9,531 and
 * 'India' 2,265), so aggregating the raw value would list India twice and
 * understate both rows. Rendering is the right place to make a code readable —
 * grouping is not. */
const COUNTRY: Record<string, string> = {
  IN: 'India', US: 'United States', ID: 'Indonesia', BR: 'Brazil',
  JP: 'Japan', GB: 'United Kingdom', PK: 'Pakistan', PH: 'Philippines',
  VN: 'Vietnam', CA: 'Canada', BD: 'Bangladesh', TH: 'Thailand',
  DE: 'Germany', FR: 'France', TR: 'Türkiye', KR: 'South Korea',
  AU: 'Australia', ES: 'Spain', MX: 'Mexico', RU: 'Russia', IT: 'Italy',
  NL: 'Netherlands', PL: 'Poland', AR: 'Argentina', CO: 'Colombia',
  EG: 'Egypt', SA: 'Saudi Arabia', NG: 'Nigeria', MY: 'Malaysia',
}
const countryName = (c: string) => COUNTRY[c] ?? c

function Breakdown({ title, rows, label, unavailable, top = 8, of }: {
  title: string
  rows: { key: string | boolean; count: number }[]
  label?: (k: string) => string
  /** Rows to show before folding the rest into one line.
   *
   *  The endpoint already returns a top-N, but four breakdowns of fifteen rows
   *  is a tile fifty rows long — which is what forced the neighbouring tile to
   *  stretch and left the classifier tab mostly empty space. Eight covers the
   *  shape of a distribution; the tail is summarised rather than dropped, so
   *  the percentages still add up to something a reader can trust. */
  top?: number
  /** Everything classified, which is what a share is a share OF. The endpoint
   *  returns a top-N, so summing its rows understated the denominator: India read
   *  23.1% of channels against a true 18.7%, and the 1.9 M channels past the 15th
   *  country were in no figure at all. Omitted where the rows are already the
   *  whole set (channel type, brand safety). */
  of?: number | undefined
  /** Set when the breakdown could not be computed at all. Distinct from an
   *  empty result: "no data" says we asked and there is nothing, which for
   *  tier_1 would be a lie — there are 3.3 M classified channels and the index
   *  simply has no aggregatable field for them yet. */
  unavailable?: string | undefined
}) {
  const total = of ?? rows.reduce((a, r) => a + r.count, 0)
  if (unavailable) {
    return (
      <div>
        <div className="sec"><h2>{title}</h2><span className="rule" /></div>
        <p className="note">{unavailable}</p>
      </div>
    )
  }
  if (!rows.length) return <p className="panel-load">no data</p>
  const shown = rows.slice(0, top)
  const shownN = shown.reduce((a, r) => a + r.count, 0)
  const restN = Math.max(0, total - shownN)
  const peak = rows[0]!.count || 1
  return (
    <div>
      <div className="sec"><h2>{title}</h2><span className="rule" /></div>
      <table className="t">
        <tbody>
          {shown.map(r => {
            const k = String(r.key)
            return (
              <tr key={k}>
                <td className="q">{label ? label(k) : k}</td>
                <td className="n">{n(r.count)}</td>
                <td className="n" style={{ width: 58 }}>
                  {pct(total ? (r.count / total) * 100 : 0)}
                </td>
                <td style={{ width: '38%' }}>
                  <div className="bar">
                    <i style={{ width: `${Math.min(100, (r.count / peak) * 100)}%` }} />
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {restN > 0 ? (
        <p className="stat-s bd-rest">
          all others · {n(restN)} ({pct(total ? (restN / total) * 100 : 0)})
        </p>
      ) : null}
    </div>
  )
}

export function ChannelHeadline() {
  const counts = useClassifiedCounts()
  const daily = useClassifierDaily()

  /* Each panel fails independently. One dead endpoint must not blank the
   * section — a board that shows nothing is indistinguishable from a pipeline
   * that did nothing. */
  return (
    <>
      <Tile title="Channels classified" hint="mirrors-classifier · from Qdrant">
        <PanelState isLoading={counts.isLoading} error={counts.error}>
          {counts.data ? (
            <>
              <div className="stats-row">
                <Stat label="total classified" value={headline(counts.data.total)} />
                <Stat label="last 1 day" value={headline(counts.data.last_1d)}
                      state={counts.data.last_1d === 0 ? 'crit' : undefined} />
                <Stat label="last 7 days" value={headline(counts.data.last_7d)} />
                <Stat label="last 30 days" value={headline(counts.data.last_30d)} />
              </div>
              {daily.data && daily.data.counts.some(c => c > 0) ? (
                <div style={{ marginTop: 14 }}>
                  <Sparkline values={daily.data.counts} label="channels classified per day" />
                  <div className="chart-key">
                    <span>{daily.data.start} → {daily.data.end}</span>
                  </div>
                </div>
              ) : null}
              {counts.data.last_1d === 0 ? (
                <p className="panel-err" style={{ marginTop: 10 }}>
                  nothing classified in 24h — the pipeline may be stopped
                </p>
              ) : null}
            </>
          ) : null}
        </PanelState>
      </Tile>

    </>
  )
}


/** The channel breakdowns, as their own tile.
 *
 *  Split from the headline so App can lay four breakdowns across the full
 *  width instead of stranding a four-stat card beside a fifty-row list — which
 *  left roughly 900 px of dead space on this tab. */
export function ChannelBreakdowns() {
  const dist = useClassifierDistributions()
  return (
    <>
      <Tile title="Where and what" hint="country · language · tier">
        <PanelState isLoading={dist.isLoading} error={dist.error}>
          {dist.data ? (
            <>
              <Breakdown title="country" rows={dist.data.countries} label={countryName} of={dist.data.total} />
              <Breakdown title="language" rows={dist.data.languages} of={dist.data.total} />
              <Breakdown
                title="tier 1"
                rows={dist.data.tier_1}
                of={dist.data.total}
                unavailable={dist.data.tier_1_ready === false
                  ? 'Unavailable — the index has no aggregatable tier_1 field '
                    + 'yet. Migration 002 adds it and backfills; until then a '
                    + 'breakdown here would report every classified channel as '
                    + 'unclassified.'
                  : undefined}
              />
              <Breakdown title="channel type" rows={dist.data.channel_type} />
              <p className="stat-s" style={{ marginTop: 12 }}>
                counts are Qdrant facets, each as a share of all classified channels
              </p>
            </>
          ) : null}
        </PanelState>
      </Tile>
    </>
  )
}


export function VideoHeadline() {
  const counts = useVideoClassified()
  const daily = useVideoDaily()
  return (
    <>
      <Tile title="videos classified" hint="vid_classification · Qdrant · created_at">
        <PanelState isLoading={counts.isLoading} error={counts.error}>
          {counts.data && (
            <>
              <div className="stats-row">
                <Stat label="total" value={headline(counts.data.total)} />
                <Stat label="last 1d" value={headline(counts.data.last_1d)} />
                <Stat label="last 7d" value={headline(counts.data.last_7d)} />
                <Stat label="last 30d" value={headline(counts.data.last_30d)} />
              </div>
              {daily.data && daily.data.counts.some(c => c > 0) ? (
                <div style={{ marginTop: 14 }}>
                  <Sparkline values={daily.data.counts} label="videos classified per day" />
                  <div className="chart-key">
                    <span>{daily.data.start} → {daily.data.end}</span>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </PanelState>
      </Tile>

    </>
  )
}


/** Video breakdowns, split from the headline for the same reason as the
 *  channel ones — see ChannelBreakdowns. */
export function VideoBreakdowns() {
  const dist = useVideoDistributions()
  const textOnlyPct = (() => {
    if (!dist.data) return null
    const t = dist.data.text_only.find(r => r.key === true)?.count ?? 0
    const f = dist.data.text_only.find(r => r.key === false)?.count ?? 0
    return t + f ? (100 * t) / (t + f) : 0
  })()
  return (
    <>
      <Tile title="video breakdowns" hint="tier · language · safety · text-only">
        <PanelState isLoading={dist.isLoading} error={dist.error}>
          {dist.data && (
            <>
              <Breakdown title="tier 1" rows={dist.data.tier_1} of={dist.data.total} />
              <Breakdown title="language" rows={dist.data.languages} of={dist.data.total} />
              <Breakdown title="brand safety" rows={dist.data.brand_safety}
                         label={k => (k === 'true' ? 'safe' : 'unsafe')} />
              {textOnlyPct !== null && (
                <p className="note">
                  {textOnlyPct.toFixed(1)}% classified without images
                  (frame fetch failed) — above ~5% means the thumbnail CDN
                  path is degrading.
                </p>
              )}
            </>
          )}
        </PanelState>
      </Tile>
    </>
  )
}

